import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { RiskCategory, RiskModelType } from "@unbox/db";
import { handleError } from "@/api/error";
import { client } from "../client";

export type CreateRiskInput = {
  id: string;
  readableName: string;
  readableDescription: string;
  category: RiskCategory;
  modelType: RiskModelType;
  defaultTier: number;
  config?: Record<string, unknown>;
  vision?: boolean;
  merchantIds?: string[];
};

export type UpdateRiskInput = {
  readableName?: string;
  readableDescription?: string;
  category?: RiskCategory;
  defaultTier?: number;
  config?: Record<string, unknown>;
  vision?: boolean;
};

const RISKS_KEY = ["admin", "risks", "all"];

// GET /admin/risks (no merchantId) — the full catalog.
export const useAllRisks = () => {
  return useQuery({
    queryKey: RISKS_KEY,
    queryFn: async () => {
      const res = await client.admin.risks.$get({ query: {} });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
  });
};

export const useCreateRisk = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateRiskInput) => {
      const res = await client.admin.risks.$post({ json: input });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RISKS_KEY });
    },
  });
};

export const useUpdateRisk = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: UpdateRiskInput }) => {
      const res = await client.admin.risks[":id"].$patch({ param: { id }, json: patch });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      // Invalidate the whole ["admin","risks",...] tree so both the full catalog
      // and the per-merchant catalog (["admin","risks","catalog",merchantId]) refetch.
      queryClient.invalidateQueries({ queryKey: ["admin", "risks"] });
    },
  });
};

// Deleting a custom risk. Pass `merchantId` to scope the delete: the risk is
// removed from that merchant and only dropped from the catalog when nothing else
// references it (response `deleted` reflects which happened). Omit `merchantId`
// for a global catalog delete (fails if the risk is still in use).
export const useDeleteRisk = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, merchantId }: { id: string; merchantId?: string }) => {
      const res = await client.admin.risks[":id"].$delete({
        param: { id },
        query: merchantId ? { merchantId } : {},
      });
      if (!res.ok) throw await handleError(res);
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "risks"] });
    },
  });
};
